<template>
  <div class="about-page">
    <div class="about-header">
      <h2>🧬 About This Project</h2>
      <p class="about-subtitle">Statistical Sample Size Calculator for Research Excellence</p>
    </div>

    <div class="about-content">
      <!-- Project Overview -->
      <section class="about-section">
        <div class="section-header">
          <h3>📊 Project Overview</h3>
        </div>
        <div class="section-content">
          <p>
            This comprehensive statistical sample size calculator is designed to empower researchers, 
            clinicians, and healthcare professionals with the tools they need to conduct robust and 
            reliable studies. Built by medical professionals with expertise in public health, digital 
            health, and AI, and supervised by Prof. Alipasha Meysamie (Full Professor at Tehran 
            University of Medical Sciences), our platform provides accurate sample size calculations 
            across a wide range of statistical methods, from basic estimation to advanced Bayesian analysis.
          </p>
          <p>
            Combining clinical expertise with modern web technologies and following best practices 
            in statistical methodology, this tool ensures that your research studies are properly 
            powered and your findings are statistically meaningful, ultimately leading to better 
            healthcare outcomes and evidence-based medicine.
          </p>
        </div>
      </section>

      <!-- Team Section -->
      <section class="about-section">
        <div class="section-header">
          <h3>👥 Our Team</h3>
        </div>
        <div class="team-grid">
          <div class="team-member">
            <div class="member-avatar">👨‍⚕️</div>
            <div class="member-info">
              <h4>Mohammad AlJasem</h4>
              <p class="member-role">MD, MPH, MSc | Digital Health Specialist</p>
              <p class="member-description">
                Medical doctor and public health specialist from Syria with advanced degrees in 
                public health and digital health. Full-stack developer passionate about AI and 
                digital health innovations. Combines clinical expertise with technical skills to 
                create impactful healthcare solutions and statistical tools for researchers.
              </p>
              <div class="member-links">
                <a href="#" class="social-link">LinkedIn</a>
                <a href="#" class="social-link">GitHub</a>
                <a href="#" class="social-link">Email</a>
              </div>
            </div>
          </div>

          <div class="team-member">
            <div class="member-avatar">👨‍🏫</div>
            <div class="member-info">
              <h4>Prof. Alipasha Meysamie</h4>
              <p class="member-role">MD, MPH | Full Professor & Project Supervisor</p>
              <p class="member-description">
                Full Professor at Tehran University of Medical Sciences, Department of Community Medicine. 
                Distinguished researcher and author of multiple books on statistical analysis. 
                Supervisor of this project, providing expert guidance on statistical methodology, 
                clinical research design, and epidemiological studies.
              </p>
              <div class="member-links">
                <a href="#" class="social-link">University Profile</a>
                <a href="#" class="social-link">Publications</a>
                <a href="#" class="social-link">Books</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Motivation Section -->
      <section class="about-section">
        <div class="section-header">
          <h3>💡 Our Motivation</h3>
        </div>
        <div class="section-content">
          <div class="motivation-grid">
            <div class="motivation-item">
              <div class="motivation-icon">🏥</div>
              <h4>Healthcare Research Impact</h4>
              <p>
                As medical professionals, we understand the critical importance of proper 
                sample size calculation in clinical research. Well-designed studies lead to 
                better patient outcomes and more reliable medical evidence.
              </p>
            </div>
            <div class="motivation-item">
              <div class="motivation-icon">🌍</div>
              <h4>Global Accessibility</h4>
              <p>
                Statistical software can be expensive and complex. We aim to provide 
                free, accessible tools that democratize statistical analysis for researchers 
                worldwide, regardless of their resources.
              </p>
            </div>
            <div class="motivation-item">
              <div class="motivation-icon">📚</div>
              <h4>Educational Impact</h4>
              <p>
                Beyond calculations, we provide educational resources to help researchers 
                understand the statistical principles behind sample size determination, 
                fostering better research practices.
              </p>
            </div>

          </div>
        </div>
      </section>

      <!-- Objectives Section -->
      <section class="about-section">
        <div class="section-header">
          <h3>🎯 Project Objectives</h3>
        </div>
        <div class="section-content">
          <div class="objectives-list">
            <div class="objective-item">
              <div class="objective-number">01</div>
              <div class="objective-content">
                <h4>Comprehensive Coverage</h4>
                <p>
                  Provide sample size calculations for all major statistical methods including 
                  estimation, hypothesis testing, correlation analysis, and advanced Bayesian approaches.
                </p>
              </div>
            </div>
            <div class="objective-item">
              <div class="objective-number">02</div>
              <div class="objective-content">
                <h4>Accuracy & Validation</h4>
                <p>
                  Ensure all calculations are mathematically correct and validated against 
                  established statistical software and peer-reviewed literature.
                </p>
              </div>
            </div>
            <div class="objective-item">
              <div class="objective-number">03</div>
              <div class="objective-content">
                <h4>User Experience</h4>
                <p>
                  Create an intuitive, responsive interface that makes complex statistical 
                  concepts accessible to users with varying levels of statistical expertise.
                </p>
              </div>
            </div>
            <div class="objective-item">
              <div class="objective-number">04</div>
              <div class="objective-content">
                <h4>Educational Resources</h4>
                <p>
                  Include comprehensive tutorials, examples, and explanations to help users 
                  understand the statistical principles and make informed decisions.
                </p>
              </div>
            </div>
            <div class="objective-item">
              <div class="objective-number">06</div>
              <div class="objective-content">
                <h4>Academic Excellence</h4>
                <p>
                  Under the supervision of Prof. Alipasha Meysamie, Full Professor at Tehran University 
                  of Medical Sciences and author of multiple statistical analysis books, ensuring 
                  academic rigor and methodological soundness.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Technology Stack -->
      <section class="about-section">
        <div class="section-header">
          <h3>🛠️ Technology Stack</h3>
        </div>
        <div class="section-content">
          <div class="tech-grid">
            <div class="tech-category">
              <h4>Frontend</h4>
              <div class="tech-tags">
                <span class="tech-tag">Vue.js 3</span>
                <span class="tech-tag">JavaScript ES6+</span>
                <span class="tech-tag">CSS3</span>
                <span class="tech-tag">Chart.js</span>
              </div>
            </div>
            <div class="tech-category">
              <h4>Statistical Engine</h4>
              <div class="tech-tags">
                <span class="tech-tag">Custom Algorithms</span>
                <span class="tech-tag">Mathematical Libraries</span>
                <span class="tech-tag">Statistical Validation</span>
              </div>
            </div>
            <div class="tech-category">
              <h4>AI & Digital Health</h4>
              <div class="tech-tags">
                <span class="tech-tag">Machine Learning</span>
                <span class="tech-tag">Healthcare AI</span>
                <span class="tech-tag">Digital Health</span>
                <span class="tech-tag">Clinical Research</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Citation Box -->
      <section class="about-section citation-box-section">
        <div class="section-header">
          <h3>📚 Citation Information</h3>
        </div>
        <div class="section-content">
          <div class="citation-box">
            <div class="citation-icon">📖</div>
            <div class="citation-content">
              <h4>How to Cite This Work</h4>
              <p>Proper citation helps us track the impact of our work and supports continued development.</p>
              <button class="citation-button" @click="showCitationModal = true">
                📋 View Citation Formats
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Contact Section -->
      <section class="about-section contact-section">
        <div class="section-header">
          <h3>📞 Get In Touch</h3>
        </div>
        <div class="section-content">
          <p>
            We welcome feedback, suggestions, and collaboration opportunities. Whether you're 
            a researcher looking for specific statistical methods, a developer interested in 
            contributing, or an educator wanting to use this tool in your courses, we'd love 
            to hear from you.
          </p>
          <div class="contact-info">
            <div class="contact-item">
              <span class="contact-icon">📧</span>
              <span>Email: mohamad@aljasem.eu.org</span>
            </div>
            <div class="contact-item">
              <span class="contact-icon">🐙</span>
              <span>GitHub: github.com/m-aljasem/sample-size-calculator</span>
            </div>
            <div class="contact-item">
              <span class="contact-icon">📄</span>
              <span>Documentation: ssc.aljasem.eu.org</span>
            </div>
          </div>
        </div>
      </section>
    </div>
    
    <!-- Citation Modal -->
    <CitationModal 
      :is-open="showCitationModal" 
      @close="showCitationModal = false" 
    />
  </div>
</template>

<script>
import CitationModal from './CitationModal.vue'

export default {
  name: 'AboutPage',
  components: {
    CitationModal
  },
  data() {
    return {
      showCitationModal: false
    }
  }
}
</script>

<style scoped>
.about-page {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin: 20px 0;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
}

.about-header {
  text-align: center;
  margin-bottom: 3rem;
  padding-bottom: 2rem;
  border-bottom: 3px solid #007BFF;
}

.about-header h2 {
  color: #007BFF;
  font-size: 2.5rem;
  margin: 0 0 1rem 0;
  font-weight: 700;
}

.about-subtitle {
  color: #666;
  font-size: 1.2rem;
  margin: 0;
  font-style: italic;
}

.about-section {
  margin-bottom: 3rem;
}

.section-header {
  margin-bottom: 1.5rem;
}

.section-header h3 {
  color: #333;
  font-size: 1.8rem;
  margin: 0;
  /* Ensure emojis are visible - no gradient text fill */
  -webkit-text-fill-color: initial !important;
  background-clip: initial !important;
  -webkit-background-clip: initial !important;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.section-content {
  color: #555;
  line-height: 1.7;
  font-size: 1rem;
}

.section-content p {
  margin-bottom: 1.5rem;
}

/* Team Section */
.team-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
}

.team-member {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 2rem;
  border: 2px solid #e9ecef;
  transition: all 0.3s ease;
}

.team-member:hover {
  border-color: #007BFF;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.15);
}

.member-avatar {
  font-size: 4rem;
  text-align: center;
  margin-bottom: 1rem;
}

.member-info h4 {
  color: #333;
  font-size: 1.5rem;
  margin: 0 0 0.5rem 0;
  font-weight: 600;
  text-align: center;
}

.member-role {
  color: #007BFF;
  font-weight: 600;
  text-align: center;
  margin: 0 0 1rem 0;
  font-size: 1rem;
}

.member-description {
  text-align: center;
  margin-bottom: 1.5rem;
  color: #666;
}

.member-links {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.social-link {
  color: #007BFF;
  text-decoration: none;
  padding: 0.5rem 1rem;
  border: 1px solid #007BFF;
  border-radius: 6px;
  transition: all 0.2s ease;
  font-size: 0.9rem;
}

.social-link:hover {
  background: #007BFF;
  color: white;
}

/* Motivation Section */
.motivation-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
}

.motivation-item {
  text-align: center;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  border: 2px solid #e9ecef;
  transition: all 0.3s ease;
}

.motivation-item:hover {
  border-color: #007BFF;
  transform: translateY(-2px);
}

.motivation-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.motivation-item h4 {
  color: #333;
  font-size: 1.3rem;
  margin: 0 0 1rem 0;
  font-weight: 600;
}

.motivation-item p {
  color: #666;
  margin: 0;
  font-size: 0.95rem;
}

/* Objectives Section */
.objectives-list {
  margin-top: 2rem;
}

.objective-item {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  border-left: 4px solid #007BFF;
}

.objective-number {
  background: #007BFF;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.objective-content h4 {
  color: #333;
  font-size: 1.3rem;
  margin: 0 0 0.5rem 0;
  font-weight: 600;
}

.objective-content p {
  color: #666;
  margin: 0;
  line-height: 1.6;
}

/* Technology Stack */
.tech-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
}

.tech-category {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 12px;
  border: 2px solid #e9ecef;
}

.tech-category h4 {
  color: #333;
  font-size: 1.2rem;
  margin: 0 0 1rem 0;
  font-weight: 600;
}

.tech-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tech-tag {
  background: #007BFF;
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
}

/* Citation Box Section */
.citation-box-section {
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  border-radius: 12px;
  padding: 2rem;
  border: 2px solid #007BFF;
}

.citation-box {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.citation-box:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.citation-icon {
  font-size: 3rem;
  flex-shrink: 0;
}

.citation-content {
  flex: 1;
}

.citation-content h4 {
  margin: 0 0 0.5rem 0;
  color: #333;
  font-size: 1.3rem;
  font-weight: 600;
}

.citation-content p {
  margin: 0 0 1rem 0;
  color: #666;
  line-height: 1.5;
}

.citation-button {
  background: #007BFF;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.2s ease;
}

.citation-button:hover {
  background: #0056b3;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

/* Contact Section */
.contact-section {
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  border-radius: 12px;
  padding: 2rem;
  border: 2px solid #007BFF;
}

.contact-info {
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 1.1rem;
  color: #333;
}

.contact-icon {
  font-size: 1.5rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .about-page {
    padding: 1rem;
    margin: 10px;
  }
  
  .about-header h2 {
    font-size: 2rem;
  }
  
  .team-grid {
    grid-template-columns: 1fr;
  }
  
  .motivation-grid {
    grid-template-columns: 1fr;
  }
  
  .objective-item {
    flex-direction: column;
    text-align: center;
  }
  
  .tech-grid {
    grid-template-columns: 1fr;
  }
  
  .contact-info {
    align-items: center;
  }
}

/* Dark Theme Support */
.dark-theme .about-page {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.dark-theme .about-header h2 {
  color: var(--accent-color);
}

.dark-theme .section-header h3 {
  color: var(--text-primary);
}

.dark-theme .team-member,
.dark-theme .motivation-item,
.dark-theme .objective-item,
.dark-theme .tech-category {
  background: var(--bg-tertiary);
  border-color: var(--border-color);
}

.dark-theme .contact-section {
  background: linear-gradient(135deg, var(--bg-tertiary) 0%, var(--bg-secondary) 100%);
  border-color: var(--accent-color);
}
</style>
